// Wire enums of the HIP records contract (ABDM-M2-HITYPES build-plan §4: encounter_kind,
// Slot.state, abha_status, upload mode, file disposition). One home so components compare against
// a constant, never an inline string. Same values as the server.
//
// Why this is its own module (not hipRecordsApi.js): component tests mock the API module
// (`jest.mock('.../hipRecordsApi')`), which would blank these constants out. Pure data, no imports.
export const ENCOUNTER_KIND = Object.freeze({ OPD: 'OPD', IPD: 'IPD' });

export const SLOT_STATE = Object.freeze({
  READY: 'READY',
  LINKING: 'LINKING',
  LINKED: 'LINKED',
  LINK_FAILED: 'LINK_FAILED',
  PAUSED: 'PAUSED',
});

// OR-114: what the list's link notice says about ONE record it tracks, from the record's slot state. LINKED = "linked";
// LINK_FAILED / PAUSED = "could not be linked"; anything else (LINKING, or a state ABDM has not answered yet) = still
// "sent to ABHA, linking...". The notice's colour, its wording and its polling all read this one mapping.
export const NOTICE_OUTCOME = Object.freeze({ LINKING: 'LINKING', LINKED: 'LINKED', FAILED: 'FAILED' });
export const noticeOutcome = (state) => {
  if (state === SLOT_STATE.LINKED) return NOTICE_OUTCOME.LINKED;
  if (state === SLOT_STATE.LINK_FAILED || state === SLOT_STATE.PAUSED) return NOTICE_OUTCOME.FAILED;
  return NOTICE_OUTCOME.LINKING;
};

// Slot.pause_reason (OR-99): why a PAUSED slot is paused (also on the 429 LINK_BLOCKED body). ABDM_BLOCKED = ABDM itself
// refused with ABDM-1027 and blocked the patient for 24 h; RATE_LIMITED = a short rate-limit pause. A missing field (or any
// other value) is shown as the plain "Paused." sentence.
export const PAUSE_REASON = Object.freeze({ ABDM_BLOCKED: 'ABDM_BLOCKED', RATE_LIMITED: 'RATE_LIMITED' });

// Slot.blocked_reason (OR-55 / QA D3 / OR-79): why a slot can NEVER be linked as it stands. null = nothing blocks it.
// RX_NOT_APPROVED = the visit's latest prescription is a draft, pending approval or rejected, so there is
// nothing to link yet; the server also sends can_link false. The FE shows it greyed, with no action.
// RX_REQUIRED (OR-79) = an OP Consultation with no approved prescription and no staff file: its record would be
// empty. can_link false; can_replace true (a staff file is the remedy - the OP Consultation pill still offers Add
// file, OR-70). The same string is the 422 code of POST /hip/records/link when such a slot is sent anyway.
export const SLOT_BLOCKED_REASON = Object.freeze({ RX_NOT_APPROVED: 'RX_NOT_APPROVED', RX_REQUIRED: 'RX_REQUIRED' });
export const isNotApproved = (slot) => slot.blockedReason === SLOT_BLOCKED_REASON.RX_NOT_APPROVED;
export const isEmptyRecord = (slot) => slot.blockedReason === SLOT_BLOCKED_REASON.RX_REQUIRED;
// Any block: the slot is not counted, not ticked, not linkable (the card pill, the rows and the button all use this).
export const isBlocked = (slot) => !!slot.blockedReason;

// "Not linked yet" for a VERIFIED patient's visit/admission: the ONE FE copy of the server's NOT_LINKED_YET rule
// (BE hip_records._status_flags, OR-86, OR-104, OR-113). True when a slot that is not blocked is READY, LINKING, LINK_FAILED
// or PAUSED (a paused slot is still not linked: it stays in this list and also shows under Link failed / All; a LINKING slot
// was sent and ABDM has not answered yet, so the card stays in this list until the record is LINKED - OR-113, amends OR-68;
// a LINKING slot is still not "Linked"), OR the row holds an unsaved invoice suggestion (a receipt / the final bill to turn
// into an invoice). A row whose only gap is a blocked slot (the empty OP Consultation, OR-79) is not. The card's state pill
// reads this, but its words come in order: Link failed, then Paused, then Not linked yet (HipEncounterCard.cardState); the
// pill keeps "Linking…" for a card whose open slots are all LINKING. Nothing else re-derives this rule.
// The list's filter and counts are the server's.
export const isNotLinkedYet = (slots, suggestionCount) => suggestionCount > 0
  || slots.some((s) => !isBlocked(s)
    && (s.state === SLOT_STATE.READY || s.state === SLOT_STATE.LINKING
      || s.state === SLOT_STATE.LINK_FAILED || s.state === SLOT_STATE.PAUSED));

// 422 code of POST /hip/records/link and /hip/invoices/{id}/link on a visit with no doctor when no doctor was sent
// and no care context stores one (OR-59, §4.7 A3). A retry sends none and relies on the stored pick, so this
// answer is what makes it ask for one.
export const PRACTITIONER_REQUIRED = 'PRACTITIONER_REQUIRED';

// Slot.source: where the slot's current file came from (APP = made in ArogyaKhata, STAFF = uploaded).
export const SLOT_SOURCE = Object.freeze({ APP: 'APP', STAFF: 'STAFF' });

export const ABHA_STATUS = Object.freeze({ VERIFIED: 'VERIFIED', NOT_VERIFIED: 'NOT_VERIFIED' });

// GET /hip/records `status` filter (4.1 + A4.1). NOT_LINKED_YET = needs linking (a PAUSED slot included, OR-104; a LINKING slot too, OR-113) OR ABHA
// not verified (OR-68), verified patients first; it is the list's default (OR-66). NEEDS_LINKING stays valid on the server but is
// no longer offered. The server's `counts` has one key per value except ALL (= the kind count).
export const LIST_STATUS = Object.freeze({
  NOT_LINKED_YET: 'NOT_LINKED_YET',
  NEEDS_LINKING: 'NEEDS_LINKING',
  LINK_FAILED: 'LINK_FAILED',
  NOT_VERIFIED: 'NOT_VERIFIED',
  LINKED: 'LINKED',
  ALL: 'ALL',
});

// The statuses the filter offers, in display order (A4.1). One list for the main list and the patient page.
export const LIST_STATUS_OPTIONS = Object.freeze([
  LIST_STATUS.NOT_LINKED_YET,
  LIST_STATUS.LINK_FAILED,
  LIST_STATUS.NOT_VERIFIED,
  LIST_STATUS.LINKED,
  LIST_STATUS.ALL,
]);

// POST /hip/records/files `mode` (4.3) - what the SERVER is asked to do. Also the mode of the inline Add file panel.
export const API_MODE = Object.freeze({ ADD: 'ADD', REPLACE: 'REPLACE' });

// GET /hip/records/file `disposition` (4.4).
export const FILE_DISPOSITION = Object.freeze({ INLINE: 'inline', ATTACHMENT: 'attachment' });

// GET /hip/records/file `file_id` that asks for every file of the slot: ONE zip when the slot
// holds several (OR-34), the file itself when it holds one.
export const FILE_ID_ALL = 'ALL';
